import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export const SIDEBAR_WIDTH_EXPANDED = 200;
export const SIDEBAR_WIDTH_COLLAPSED = 60;
/** Delay before sidebar text reveals after expanding — lets the width animation get past the halfway mark. */
export const SIDEBAR_LABELS_REVEAL_DELAY = 180;

const STORAGE_KEY = 'arbor-sidebar-collapsed';

interface SidebarContextValue {
  collapsed: boolean;
  /** True only when the sidebar is expanded AND the reveal delay has elapsed (text-safe to render). */
  showLabels: boolean;
  sidebarWidth: number;
  toggle: () => void;
  setCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  showLabels: true,
  sidebarWidth: SIDEBAR_WIDTH_EXPANDED,
  toggle: () => {},
  setCollapsed: () => {},
});

export const useSidebarState = () => useContext(SidebarContext);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const setCollapsedValue = useCallback((value: boolean) => {
    setCollapsed(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* noop */
    }
  }, []);

  const sidebarWidth = collapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  // Labels reveal *after* the width animation when expanding; hide immediately when collapsing.
  const [showLabels, setShowLabels] = useState(!collapsed);
  useEffect(() => {
    if (collapsed) {
      setShowLabels(false);
      return;
    }
    const timer = setTimeout(
      () => setShowLabels(true),
      SIDEBAR_LABELS_REVEAL_DELAY,
    );
    return () => clearTimeout(timer);
  }, [collapsed]);

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        showLabels,
        sidebarWidth,
        toggle,
        setCollapsed: setCollapsedValue,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
